/* eslint-disable */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';

import { Order } from '../../lib/models/trading/types';
import { DAILY_FX_DOMAIN, DAILY_MAX_DOMAIN_EU_STOCKS, DAILY_MAX_DOMAIN_US_STOCKS } from '../../lib/store/market-data/charting/constants';
import { calculateYLabelValue, tickFormatCalculation } from '../../lib/store/market-data/charting/helpers';
import { ChartDataRange, ChartDataSet } from '../../lib/store/market-data/charting/types';
import ChartHoverCache from '../../lib/store-util/ChartHoverCache';
import { formatNumber } from '../../lib/util/DataHelpers';

import {
  CHART_HEIGHT_MOBILE,
  CHART_HEIGHT_WEB,
  CHART_PADDING,
  CHART_PADDING_MOBILE,
  CHART_WIDTH,
  VOLUME_HEIGHT_RATIO,
  Y_AXIS_TICKS} from './constants';

import styles from './CanvasChart.module.scss';
import useIsMobile from '../hooks/useIsMobile';

type Props = {
  dataSet: ChartDataSet;
  range: ChartDataRange;
  isEuroStock: boolean;
  isFx?: boolean;
  isCrypto?: boolean;
  lineClose: number;
  width?: number;
  height?: number;
  symbol?: string;
  companyName?: string;
};

type TooltipData = {
  x: number;
  y: number;
  price: number;
  time: string;
  volume: number;
  orders?: Order[];
};

const PRICE_LABEL_COMPACT_THRESHOLD = 10_000;
const PRICE_LABEL_THOUSANDS_THRESHOLD = 1000;
const PRICE_LABEL_DIVISOR_THOUSAND = 1000;
const SUB_UNIT_PRICE_THRESHOLD = 1;
const SUB_UNIT_PRICE_MIN_DECIMALS = 2;
const SUB_UNIT_PRICE_MAX_DECIMALS = 4;
const FX_PRICE_DECIMALS = 4;
const ZERO_VALUE = 0;

const subUnitPriceFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: SUB_UNIT_PRICE_MIN_DECIMALS,
  maximumFractionDigits: SUB_UNIT_PRICE_MAX_DECIMALS,
});

const formatSubUnitPrice = (value: number): string => {
  const roundedValue = Number(value.toFixed(SUB_UNIT_PRICE_MAX_DECIMALS));
  const normalizedValue = Object.is(roundedValue, -0) ? ZERO_VALUE : roundedValue;
  return subUnitPriceFormatter.format(normalizedValue);
};

const formatPriceAxisLabel = (value: number): string => {
  const absValue = Math.abs(value);

  if (absValue >= PRICE_LABEL_COMPACT_THRESHOLD) {
    const k = value / PRICE_LABEL_DIVISOR_THOUSAND;
    return `${k.toFixed(1)}K`;
  }
  if (absValue >= PRICE_LABEL_THOUSANDS_THRESHOLD) {
    return Math.round(value).toLocaleString('en-US');
  }
  if (absValue < SUB_UNIT_PRICE_THRESHOLD) {
    return formatSubUnitPrice(value);
  }
  return value.toFixed(1);
};

const formatLineCloseLabel = (value: number, isFx: boolean): string => {
  if (isFx) {
    return value.toFixed(FX_PRICE_DECIMALS);
  }

  if (Math.abs(value) < SUB_UNIT_PRICE_THRESHOLD) {
    return formatSubUnitPrice(value);
  }

  return formatNumber(value);
};

export const CanvasChart = ({
  dataSet,
  range,
  isEuroStock,
  isFx = false,
  isCrypto = false,
  lineClose,
  width: propWidth,
  height: propHeight,
  symbol,
  companyName,
}: Props) => {
  const { isMobile } = useIsMobile();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const lastTouchEndTimeRef = useRef(0);
  const isScrubbingRef = useRef(false);
  const tooltipDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [tooltipWidth, setTooltipWidth] = useState(0);
  const [dimensions, setDimensions] = useState({ width: CHART_WIDTH, height: CHART_HEIGHT_WEB });

  // Compute dynamic right padding for mobile by measuring the lineClose label.
  // Using lineClose (not tick labels) as the reference keeps padding stable across
  // timerange switches — lineClose is the same value regardless of which range is active.
  const rightPadding = useMemo(() => {
    if (!isMobile) return CHART_PADDING.right;

    const label = formatLineCloseLabel(lineClose, isFx || isCrypto);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return CHART_PADDING_MOBILE.right;
    ctx.font = '11px Manrope-Regular';

    const labelWidth = ctx.measureText(label).width;
    // 4px label x-offset from canvas edge + measured width + 10px gap.
    // Minimum of 40px so very short labels (e.g. "9.3") still have breathing room.
    return Math.max(40, Math.ceil(labelWidth) + 14);
  }, [isMobile, isFx, lineClose]);

  const padding = useMemo(() => {
    const base = isMobile ? CHART_PADDING_MOBILE : CHART_PADDING;
    return isMobile ? { ...base, right: rightPadding } : base;
  }, [isMobile, rightPadding]);

  // Get expected maximum number of data points for proportional scaling
  const getExpectedMaxPoints = useCallback((): number => {
    // For non-1D ranges, use actual data length
    if (range !== '1D') return dataSet.lineData.length;

    // For 1D range, use expected full-day points based on market
    if (isFx || isCrypto) return DAILY_FX_DOMAIN;
    return isEuroStock ? DAILY_MAX_DOMAIN_EU_STOCKS : DAILY_MAX_DOMAIN_US_STOCKS;
  }, [range, isFx, isCrypto, isEuroStock, dataSet.lineData.length]);

  // Handle responsive sizing
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const newWidth = propWidth ?? containerWidth;
      const newHeight = propHeight ?? (isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT_WEB);

      setDimensions({ width: newWidth, height: newHeight });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, [propWidth, propHeight, isMobile]);

  // Attach a non-passive touchmove listener directly to the canvas so we can call
  // preventDefault() when needed. React attaches synthetic handlers as passive:true
  // (browsers ignore preventDefault() on passive listeners), which is why the native
  // listener is required here.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const onNativeTouchMove = (event: TouchEvent) => {
      const start = touchStartRef.current;
      if (!start) return;

      const dx = Math.abs(event.touches[0].clientX - start.x);
      const dy = Math.abs(event.touches[0].clientY - start.y);
      const isHorizontalGesture = dx > dy;

      // Block native scroll once scrubbing is active or gesture is clearly horizontal.
      // isScrubbingRef is updated synchronously in handleTouchMove so it is always current.
      if (isScrubbingRef.current || isHorizontalGesture) {
        event.preventDefault();
      }
    };

    canvas.addEventListener('touchmove', onNativeTouchMove, { passive: false });
    return () => canvas.removeEventListener('touchmove', onNativeTouchMove);
  }, []); // intentionally empty — handler reads only refs

  const getChartBounds = useCallback(() => {
    const chartWidth = dimensions.width - padding.left - padding.right;
    const chartHeight = dimensions.height - padding.top - padding.bottom;
    return { chartWidth, chartHeight };
  }, [dimensions, padding]);

  const getYRange = useCallback(() => {
    const { lineData } = dataSet;
    if (isFx || isCrypto) {
      // FX and Crypto: extend bottom if we have volume bars, otherwise no
      const closePrices = [...lineData.map(pt => pt.close), lineClose].sort((a, b) => a - b);
      const minPrice = closePrices[0] * 0.998;
      const maxPrice = closePrices[closePrices.length - 1] * 1.001;
      
      if (isCrypto) {
        // Crypto has volume bars, so extend bottom
        const priceRange = maxPrice - minPrice;
        const minDomain = minPrice - (priceRange * 0.2);
        return { min: minDomain, max: maxPrice, priceMin: minPrice, priceMax: maxPrice };
      }
      
      return { min: minPrice, max: maxPrice, priceMin: minPrice, priceMax: maxPrice };
    }
    if (isEuroStock) {
      const closePrices = [...lineData.map(pt => pt.close), lineClose].sort((a, b) => a - b);
      const minPrice = closePrices[0] * 0.998;
      const maxPrice = closePrices[closePrices.length - 1] * 1.001;
      const priceRange = maxPrice - minPrice;
      // Extend bottom for volume bars
      const minDomain = minPrice - (priceRange * 0.2);
      return { min: minDomain, max: maxPrice, priceMin: minPrice, priceMax: maxPrice };
    }

    // For US stocks, use normalized data.
    // If the current min (normalized 0) would extrapolate to a negative price, clamp it
    // to the normalized value corresponding to price $0 so the chart never goes below zero.
    const normalizedZeroPrice = calculateYLabelValue(0, dataSet.lineDiff, dataSet.minLineValue, dataSet.lineDataRatio);
    return {
      min: Math.max(0, normalizedZeroPrice),
      max: dataSet.maxChartPointWeb,
      priceMin: dataSet.minLineValue,
      priceMax: dataSet.maxLineValue || dataSet.minLineValue,
    };
  }, [dataSet, isEuroStock, isFx, lineClose]);

  const scaleX = useCallback((index: number, totalPoints: number): number => {
    const { chartWidth } = getChartBounds();

    // For 1D range, scale based on expected full-day points to prevent stretching.
    // Use the larger of the two so the last real data point never exceeds chartWidth.
    if (range === '1D') {
      // const maxPoints = getExpectedMaxPoints();
      const maxPoints = Math.max(getExpectedMaxPoints(), totalPoints);
      return padding.left + ((index / (maxPoints - 1)) * chartWidth);
    }

    // For other ranges, use actual data length
    return padding.left + ((index / (totalPoints - 1)) * chartWidth);
  }, [getChartBounds, padding, range, getExpectedMaxPoints]);

  const scaleY = useCallback((value: number): number => {
    const { chartHeight } = getChartBounds();
    const { min, max } = getYRange();
    const yRange = max - min;
    return (padding.top + chartHeight) - (((value - min) / yRange) * chartHeight);
  }, [getChartBounds, getYRange, padding]);

  const drawYAxis = useCallback((ctx: CanvasRenderingContext2D) => {
    const { min, max, priceMin, priceMax } = getYRange();
    const { chartWidth } = getChartBounds();

    // Draw horizontal grid lines first (so they appear behind labels)
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 0.5;
    ctx.setLineDash([5, 5]); // Dashed line: 5px dash, 5px gap

    for (let i = 0; i < Y_AXIS_TICKS; i += 1) {
      if (i === 0) continue; // skip bottom line — it overlaps the X-axis
      const value = min + (((max - min) * i) / (Y_AXIS_TICKS - 1));
      const y = scaleY(value);

      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(padding.left + chartWidth, y);
      ctx.stroke();
    }
    ctx.restore();

    // Draw Y-axis labels
    ctx.fillStyle = '#A0A3B9';
    ctx.font = isMobile ? '11px Manrope-Regular' : '10px Manrope-Regular';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < Y_AXIS_TICKS; i += 1) {
      if (i === 0) continue;
      const value = min + (((max - min) * i) / (Y_AXIS_TICKS - 1));
      const y = scaleY(value);

      // Calculate actual price value for display
      let displayValue: number;
      if (isEuroStock || isFx || isCrypto) {
        displayValue = value;
      } else {
        // Use the exact inverse of calculateYLabelValue to convert normalized Y back to price
        displayValue = tickFormatCalculation(value, dataSet.lineDiff, dataSet.minLineValue, dataSet.lineDataRatio);
      }

      let label: string;
      if (isFx || isCrypto) {
        label = displayValue.toFixed(4);
      } else {
        label = formatPriceAxisLabel(displayValue);
      }

      const x = dimensions.width - 4;
      ctx.fillText(label, x, y);
    }
  }, [getYRange, scaleY, padding.left, padding.right, dimensions.width, isEuroStock, isFx, isMobile, getChartBounds]);  

  const drawXAxis = useCallback((ctx: CanvasRenderingContext2D) => {
    const { chartWidth } = getChartBounds();
    const { axisValue } = dataSet;

    // FX 1D: 5-min candles are too dense — use a fixed 4-hour interval for tick placement
    const getFxTickValues = (): number[] => {
      const totalPoints = dataSet.lineData.length;
      const result: number[] = [];
      for (let i = 0; i < totalPoints; i += 48) {
        result.push(i);
      }
      return result;
    };
    const getWholeHourTicks = (): number[] => (
      axisValue.reduce((acc: number[], label, idx) => {
        if (label?.endsWith(':00')) acc.push(idx);
        return acc;
      }, [])
    );
    const overrideLabels = new Map<number, string>();

    const formatAxisDate = (dateTime: string): string => {
      const d = new Date(dateTime);
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
    };

    // All ranges: always 5 ticks at fixed proportional positions (~17%, 33%, 50%, 67%, 83%)
    // so the grid never shifts or changes count when switching timeframes.
    const getFixedTicks = (): number[] => {
      const TICK_COUNT = 6;
      const totalPoints = dataSet.lineData.length;
      const result: number[] = [];
      for (let i = 1; i <= TICK_COUNT; i += 1) {
        const idx = Math.floor(((2 * i - 1) / (2 * TICK_COUNT + 1)) * totalPoints);
        const clampedIdx = Math.max(0, Math.min(idx, totalPoints - 1));
        const point = dataSet.lineData[clampedIdx];
        if (!point?.dateAndTime) continue;
        result.push(clampedIdx);
        if (range === '1Y') {
          overrideLabels.set(clampedIdx, new Date(point.dateAndTime).toLocaleDateString('en-US', { month: 'short' }));
        } else if (range === '5Y') {
          overrideLabels.set(clampedIdx, String(new Date(point.dateAndTime).getFullYear()));
        } else {
          overrideLabels.set(clampedIdx, formatAxisDate(point.dateAndTime));
        }
      }
      return result;
    };
    let activeTicks: number[];
    if ((isFx || isCrypto) && range === '1D') {
      activeTicks = getFxTickValues();
    } else if (range === '1D') {
      const hourTicks = getWholeHourTicks();
      activeTicks = (isEuroStock && isMobile) ? hourTicks.slice(1) : hourTicks;
    } else {
      activeTicks = getFixedTicks();
    }

    ctx.fillStyle = '#8B94A3';
    ctx.strokeStyle = '#256FA1';
    ctx.font = isMobile ? '10px Manrope-Regular' : '10px Manrope-Regular';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    const y = dimensions.height - padding.bottom + 10;

    // Draw vertical grid lines at each X-axis tick position
    ctx.save();
    ctx.strokeStyle = '#3BB0FF33';
    ctx.lineWidth = 0.5;
    ctx.setLineDash([5, 5]); // Dashed line: 5px dash, 5px gap

    activeTicks.forEach((tickIndex) => {
      const label = overrideLabels.get(tickIndex) ?? axisValue[tickIndex];
      if (!label) return; // Only draw grid lines where time labels exist

      const x = scaleX(tickIndex, dataSet.lineData.length);
      if (x > padding.left + chartWidth) return; // Skip ticks beyond chart bounds

      ctx.beginPath();
      ctx.moveTo(x, padding.top);
      ctx.lineTo(x, dimensions.height - padding.bottom);
      ctx.stroke();
    });

    ctx.restore();

    activeTicks.forEach((tickIndex, loopIndex) => {
      const label = overrideLabels.get(tickIndex) ?? axisValue[tickIndex];
      if (!label) return;

      const x = scaleX(tickIndex, dataSet.lineData.length);
      if (x > padding.left + chartWidth) return; // Skip ticks beyond chart bounds

      // Draw tick mark
       
      ctx.strokeStyle = '#3BB0FF33';
      ctx.beginPath();
      ctx.moveTo(x, dimensions.height - padding.bottom);
      ctx.lineTo(x, dimensions.height - padding.bottom + 5);
      ctx.stroke();
       
      ctx.strokeStyle = '#256FA1';

      // On web, left-align the first label and add a small offset to prevent it being clipped at the left edge
      const useLeftAlign = loopIndex === 0 && !isMobile && (isFx || isCrypto || (isEuroStock && range === '1D'));
       
      if (useLeftAlign) ctx.textAlign = 'left';
      ctx.fillText(label, x, y);
       
      if (useLeftAlign) ctx.textAlign = 'center';
    });

    // Draw X-axis line (solid, not dashed)
    ctx.setLineDash([]);
    ctx.strokeStyle = '#3BB0FF33';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padding.left, dimensions.height - padding.bottom);
    ctx.lineTo(padding.left + chartWidth, dimensions.height - padding.bottom);
    ctx.stroke();

    return undefined;
  }, [getChartBounds, dataSet, scaleX, dimensions.height, padding, isFx, isMobile, range, isEuroStock]);

  const drawVolumeBars = useCallback((ctx: CanvasRenderingContext2D) => {
    const { lineData, rawData } = dataSet;
    const { min, max, priceMin, priceMax } = getYRange();
    const priceRangeValue = priceMax - priceMin;

    const rawVolumes = rawData.map(d => d.volume || 0);
    const maxVol = Math.max(...rawVolumes);

    ctx.fillStyle = '#3BB0FF33';

    rawData.forEach((d, idx) => {
      const vol = d.volume || 0;

      let barY: number;
      let barHeight: number;

      if (isEuroStock || isCrypto) {
        // Volume bars should render at bottom like US stocks
        const volumeZoneHeight = priceRangeValue * VOLUME_HEIGHT_RATIO;
        const height = (vol / maxVol) * volumeZoneHeight;
        // Start from chart bottom and extend upward
        const chartBottomPrice = priceMin - (priceRangeValue * 0.2);
        const volumeTopPrice = chartBottomPrice + height;
        barY = scaleY(volumeTopPrice) - 2; // Top of the volume bar, lifted 2px
        barHeight = scaleY(chartBottomPrice) - scaleY(volumeTopPrice); // original height
      } else {
        // For US stocks, volume bars at bottom — baseline is min (canvas bottom)
        const volumeZoneHeight = (max - min) * VOLUME_HEIGHT_RATIO;
        const height = (vol / maxVol) * volumeZoneHeight;
        barY = scaleY(min + height) - 1;
        barHeight = scaleY(min) - scaleY(min + height);
      }

      const x = scaleX(idx, lineData.length);
      const barWidth = isMobile ? 2 : 4;

      ctx.fillRect(x - (barWidth / 2), barY, barWidth, Math.abs(barHeight));
    });
  }, [dataSet, getYRange, scaleX, scaleY, isEuroStock, isMobile]);

  const drawLineChart = useCallback((ctx: CanvasRenderingContext2D) => {
    const { lineData } = dataSet;
    if (lineData.length === 0) {
      return undefined;
    }

    const firstPoint = lineData[0];
    const lastPoint = lineData[lineData.length - 1];
    // For 1D, use lineClose (previous day's close). For other ranges, compare range start vs end
    const referencePrice = range === '1D' ? lineClose : firstPoint.close;
    const fillColor = referencePrice > lastPoint.close ? '#F96C75' : '#1BFEBA';

    // Clip to chart area so 1D after-hours data never overflows into the right padding.
    const { chartWidth, chartHeight } = getChartBounds();
    ctx.save();
    ctx.beginPath();
    ctx.rect(padding.left, padding.top, chartWidth, chartHeight);
    ctx.clip();

    // Draw gradient fill
    const gradient = ctx.createLinearGradient(0, padding.top, 0, padding.top + chartHeight);
    gradient.addColorStop(0, `${fillColor}40`);
    gradient.addColorStop(1, `${fillColor}00`);

    ctx.beginPath();
    lineData.forEach((point, index) => {
      const x = scaleX(index, lineData.length);
      const y = scaleY(point.y);

      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    // Complete the area path
    const lastX = scaleX(lineData.length - 1, lineData.length);
    const firstX = scaleX(0, lineData.length);
    const bottomY = scaleY(getYRange().min);

    ctx.lineTo(lastX, bottomY);
    ctx.lineTo(firstX, bottomY);
    ctx.closePath();

    ctx.fillStyle = gradient;
    ctx.fill();

    // Draw line - different approach when tooltip is active
    if (tooltip) {
      // Step 1: Draw entire line in darker color
      ctx.beginPath();
      lineData.forEach((point, index) => {
        const x = scaleX(index, lineData.length);
        const y = scaleY(point.y);

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.strokeStyle = fillColor === '#F96C75' ? 'rgba(249, 108, 117, 0.3)' : 'rgba(27, 254, 186, 0.3)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Step 2: Draw glowing segment near hover point with graduated fade
      const hoverIndex = tooltip.x;

      ctx.save();

      // Draw multiple graduated segments for smooth fade-out along the line
      const segments = [
        { range: 8, blur: 16, opacity: 0.15, width: 3 }, // Outermost (faintest)
        { range: 6, blur: 12, opacity: 0.25, width: 3 },
        { range: 4, blur: 8, opacity: 0.4, width: 3 },
        { range: 3, blur: 5, opacity: 0.6, width: 3 },
        { range: 2, blur: 2, opacity: 1, width: 3 }, // Innermost (brightest)
      ];

      segments.forEach(segment => {
        const startIndex = Math.max(0, hoverIndex - segment.range);
        const endIndex = Math.min(lineData.length - 1, hoverIndex + segment.range);

        ctx.shadowColor = fillColor;
        ctx.shadowBlur = segment.blur;
        ctx.strokeStyle = fillColor;
        ctx.globalAlpha = segment.opacity;
        ctx.lineWidth = segment.width;

        ctx.beginPath();
        for (let i = startIndex; i <= endIndex; i += 1) {
          const point = lineData[i];
          const x = scaleX(i, lineData.length);
          const y = scaleY(point.y);
          if (i === startIndex) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      });

      /* eslint-enable no-param-reassign */
      ctx.restore();
    } else {
      // Normal line drawing when no tooltip
      ctx.beginPath();
      lineData.forEach((point, index) => {
        const x = scaleX(index, lineData.length);
        const y = scaleY(point.y);

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      /* eslint-disable no-param-reassign */
      ctx.strokeStyle = fillColor;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    ctx.restore(); // remove chart-area clip

    return undefined;
  }, [dataSet, scaleX, scaleY, lineClose, getChartBounds, getYRange, padding, tooltip, range]);

  const drawLineClose = useCallback((ctx: CanvasRenderingContext2D) => {
    const { chartWidth } = getChartBounds();

    let lineCloseY: number;
    if (isEuroStock || isFx || isCrypto) {
      lineCloseY = scaleY(lineClose);
    } else {
      // For US stocks, need to convert lineClose to normalized value
      const { lineDiff, minLineValue, lineDataRatio } = dataSet;
      const normalizedLineClose = calculateYLabelValue(lineClose, lineDiff, minLineValue, lineDataRatio);
      lineCloseY = scaleY(normalizedLineClose);
    }

    // Draw dashed line
    /* eslint-disable no-param-reassign */
    ctx.strokeStyle = '#c6c8da';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(padding.left, lineCloseY);
    ctx.lineTo(padding.left + chartWidth, lineCloseY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw label
    const label = formatLineCloseLabel(lineClose, isFx || isCrypto);
    ctx.font = isMobile ? '11px Manrope-Regular' : '12px Manrope-Regular';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';

    const labelPadding = 4;
    const textMetrics = ctx.measureText(label);
    const { actualBoundingBoxLeft: textLeft, actualBoundingBoxRight: textRight,
            actualBoundingBoxAscent: textAscent, actualBoundingBoxDescent: textDescent } = textMetrics;

    // Size the pill from actual glyph bounds so padding is equal on all sides
    const bgWidth = textLeft + textRight + labelPadding * 2;
    const bgHeight = textAscent + textDescent + labelPadding * 2;
    const bgX = dimensions.width - bgWidth;
    const bgY = lineCloseY - bgHeight / 2;

    ctx.fillStyle = '#0A0B13';
    ctx.beginPath();
    ctx.roundRect(bgX, bgY, bgWidth, bgHeight, 6);
    ctx.fill();

    // Place origin so glyph visual top-left lands at (bgX + labelPadding, bgY + labelPadding)
    ctx.fillStyle = 'white';
    ctx.fillText(label, bgX + labelPadding + textLeft, bgY + labelPadding + textAscent);
  }, [dataSet, getChartBounds, scaleY, lineClose, padding, isEuroStock, isFx, isMobile, dimensions.width]);  

  const drawWatermark = useCallback((ctx: CanvasRenderingContext2D) => {
    if (!symbol && !companyName) return;

    const { chartWidth, chartHeight } = getChartBounds();
    const cx = padding.left + chartWidth / 2;

    const symbolFontSize = isMobile ? 30 : 40;
    const nameFontSize = isMobile ? 12 : 14;
    const lineGap = 8;

    const totalHeight = symbol && companyName
      ? symbolFontSize + lineGap + nameFontSize
      : (symbol ? symbolFontSize : nameFontSize);

    // Center in the price area above the volume-bar zone
    const priceAreaHeight = isFx ? chartHeight : chartHeight * (1 - VOLUME_HEIGHT_RATIO);
    const verticalShift = isMobile ? 20 : 0;
    const areaCenter = padding.top + priceAreaHeight / 2 + verticalShift;
    const startY = areaCenter - totalHeight / 2;

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    if (symbol) {
      ctx.globalAlpha = 0.25;
      ctx.fillStyle = '#3BB0FF';
      ctx.font = `${symbolFontSize}px Manrope-Bold`;
      ctx.fillText(symbol, cx, startY);
    }

    if (companyName) {
      ctx.globalAlpha = 0.25;
      ctx.fillStyle = '#3BB0FF';
      const nameY = symbol ? startY + symbolFontSize + lineGap : startY;
      ctx.font = `${nameFontSize}px Manrope-Regular`;

      let displayName = companyName;
      if (isMobile) {
        const maxWidth = chartWidth * 0.8;
        if (ctx.measureText(companyName).width > maxWidth) {
          let truncated = companyName;
          while (truncated.length > 0 && ctx.measureText(`${truncated}...`).width > maxWidth) {
            truncated = truncated.slice(0, -1);
          }
          displayName = `${truncated}...`;
        }
      }

      ctx.fillText(displayName, cx, nameY);
    }

    ctx.restore();
  }, [symbol, companyName, getChartBounds, padding, isMobile, isFx]);

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, dimensions.width, dimensions.height);

    // Set high DPI scaling
    const dpr = window.devicePixelRatio || 1;
    canvas.width = dimensions.width * dpr;
    canvas.height = dimensions.height * dpr;
    ctx.scale(dpr, dpr);

    // Draw chart elements
    drawWatermark(ctx);
    if (!isFx) drawVolumeBars(ctx);
    drawLineChart(ctx);
    drawYAxis(ctx);
    drawXAxis(ctx);
    drawLineClose(ctx);

    // Draw hover indicator
    if (tooltip) {
      const x = scaleX(tooltip.x, dataSet.lineData.length);
      const y = scaleY(tooltip.y);

      // Draw vertical line
      ctx.strokeStyle = '#A0A3B9';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, padding.top);
      ctx.lineTo(x, dimensions.height - padding.bottom);
      ctx.stroke();

      // Draw dot
      const firstPointData = dataSet.lineData[0];
      const lastPointData = dataSet.lineData[dataSet.lineData.length - 1];
      // For 1D, use lineClose (previous day's close). For other ranges, compare range start vs end
      const referencePriceForColor = range === '1D' ? lineClose : firstPointData.close;
      const fillColor = referencePriceForColor > lastPointData.close ? '#F96C75' : '#1BFEBA';
      ctx.fillStyle = '#000';
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(x, y, isMobile ? 4 : 5, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    }
  }, [
    dimensions, drawWatermark, drawVolumeBars, drawLineChart, drawLineClose, drawYAxis, drawXAxis,
    tooltip, scaleX, scaleY, dataSet, padding, lineClose, isFx, isMobile, range,
  ]);

  useLayoutEffect(() => {
    drawChart();
  }, [drawChart]);

  // Shared logic: resolve a clientX position to the nearest data point and show the tooltip.
  const setTooltipFromClientX = useCallback((clientX: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    // rect.width is in visual (zoomed) pixels; dimensions.width is in logical CSS pixels.
    // When CSS zoom != 1 (e.g. 0.9 on screens < 340px), dividing by the ratio converts
    // the viewport-pixel offset back to logical coordinates before indexing into the chart.
    const zoomScale = rect.width / dimensions.width;
    const relativeX = (clientX - rect.left) / zoomScale - padding.left;
    const { chartWidth } = getChartBounds();
    const { lineData } = dataSet;

    const effectiveMaxPoints = Math.max(getExpectedMaxPoints(), lineData.length);
    const maxRightX = range === '1D'
      ? Math.max(chartWidth, ((lineData.length - 1) / (effectiveMaxPoints - 1)) * chartWidth)
      : chartWidth;

    if (relativeX < 0 || relativeX > maxRightX) {
      setTooltip(null);
      return;
    }

    let index: number;
    if (range === '1D') {
      index = Math.round((relativeX / chartWidth) * (effectiveMaxPoints - 1));
      if (index >= lineData.length) index = lineData.length - 1;
    } else {
      index = Math.round((relativeX / chartWidth) * (lineData.length - 1));
    }

    if (index >= 0 && index < lineData.length) {
      const point = lineData[index];
      setTooltip({
        x: index,
        y: point.y,
        price: point.close,
        time: point.dateAndTime,
        volume: point.volume,
      });
      ChartHoverCache.set({
        price: point.close,
        time: point.dateAndTime,
        volume: point.volume,
        isHovering: true,
      });
    }
  }, [dataSet, getChartBounds, padding.left, range, getExpectedMaxPoints, dimensions.width]);

  const handleMouseMove = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    // iOS fires synthetic mousemove events ~300ms after touchend. Ignore them so a
    // quick tap doesn't leave the tooltip stuck open with no way to dismiss it.
    if (Date.now() - lastTouchEndTimeRef.current < 500) return;
    setTooltipFromClientX(event.clientX);
  }, [setTooltipFromClientX]);

  const handleMouseLeave = useCallback(() => {
    setTooltip(null);
    ChartHoverCache.clear();
  }, []);

  const handleTouchMove = useCallback((event: React.TouchEvent<HTMLCanvasElement>) => {
    // Once the tooltip is visible, keep it alive and track horizontal position for the
    // entire gesture — only touchend should dismiss it. Vertical movement is already
    // blocked by the native non-passive listener while isScrubbingRef is true.
    if (isScrubbingRef.current) {
      setTooltipFromClientX(event.touches[0].clientX);
      return;
    }

    const start = touchStartRef.current;
    if (start) {
      const dx = Math.abs(event.touches[0].clientX - start.x);
      const dy = Math.abs(event.touches[0].clientY - start.y);
      // Vertical gesture before tooltip appeared: cancel the pending timer and let scroll through
      if (dy > dx) {
        if (tooltipDelayTimerRef.current !== null) {
          clearTimeout(tooltipDelayTimerRef.current);
          tooltipDelayTimerRef.current = null;
        }
        return;
      }
    }

    // Horizontal gesture before tooltip appeared: cancel the delay and show immediately
    if (tooltipDelayTimerRef.current !== null) {
      clearTimeout(tooltipDelayTimerRef.current);
      tooltipDelayTimerRef.current = null;
    }
    isScrubbingRef.current = true;
    setTooltipFromClientX(event.touches[0].clientX);
  }, [setTooltipFromClientX]);

  const handleTouchStart = useCallback((event: React.TouchEvent<HTMLCanvasElement>) => {
    touchStartRef.current = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
    };
    // Delay tooltip appearance slightly so a quick vertical scroll doesn't flash it.
    // If the finger moves vertically before the timer fires, handleTouchMove cancels it.
    tooltipDelayTimerRef.current = setTimeout(() => {
      tooltipDelayTimerRef.current = null;
      // Lock scroll as soon as the tooltip appears, before any horizontal movement.
      isScrubbingRef.current = true;
      setTooltipFromClientX(event.touches[0].clientX);
    }, 150);
  }, [setTooltipFromClientX]);

  const handleTouchEnd = useCallback(() => {
    if (tooltipDelayTimerRef.current !== null) {
      clearTimeout(tooltipDelayTimerRef.current);
      tooltipDelayTimerRef.current = null;
    }
    lastTouchEndTimeRef.current = Date.now();
    touchStartRef.current = null;
    isScrubbingRef.current = false;
    setTooltip(null);
    ChartHoverCache.clear();
  }, []);

  const formatTooltipTime = (dateTime: string): string => {
    const formats: Record<ChartDataRange, (dt: string) => string> = {
      '1D': (dt) => {
        // Parse UTC datetime and convert to local time, matching X-axis behavior
        const utcMoment = moment.utc(dt);
        return utcMoment.local().format('HH:mm');
      },
      '1W': (dt) => moment.utc(dt).local().format('HH:mm, DD MMM'),
      '1M': (dt) => moment.utc(dt).local().format('HH:mm, DD MMM'),
      '3M': (dt) => moment(dt).format('DD MMM YYYY'),
      '1Y': (dt) => moment(dt).format('DD MMM YYYY'),
      '5Y': (dt) => moment(dt).format('MMM YYYY'),
      YTD: (dt) => moment(dt).format('YY-MM-DD'),
      ALL: (dt) => moment(dt).format('YY-MM-DD'),
    };

    return formats[range]?.(dateTime) || dateTime;
  };

  useLayoutEffect(() => {
    if (!tooltip || !tooltipRef.current) {
      setTooltipWidth(0);
      return;
    }

    const measuredWidth = tooltipRef.current.getBoundingClientRect().width;
    setTooltipWidth(measuredWidth);
  }, [tooltip, range, dimensions.width]);

  return (
    <div ref={containerRef} className={styles.container}>
      <canvas
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        style={{ width: dimensions.width, height: dimensions.height }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={styles.canvas}
      />
      {tooltip && (
        <div
          ref={tooltipRef}
          className={styles.tooltip}
          style={{
            left: Math.max(
              (tooltipWidth / 2) - 6,
              Math.min(
                dimensions.width - (tooltipWidth / 2),
                scaleX(tooltip.x, dataSet.lineData.length),
              ),
            ),
            top: padding.top - 28,
          }}
        >
          <div className={styles.tooltipTime}>
            {formatTooltipTime(tooltip.time)}
          </div>
        </div>
      )}
    </div>
  );
};
